import React, { useState, useEffect } from "react";
import { useNavigate, Navigate, useParams } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MapPicker from "@/components/MapPicker";
import VastuPanel from "@/components/VastuPanel";
import { toast } from "sonner";
import { Upload, Trash2, ImagePlus, FileText, Boxes, Loader2, Compass } from "lucide-react";

const CATS = ["ghar", "plot", "shop", "jamin"];
const SIZE_UNITS = ["sqft", "gaj", "acre", "bigha", "sqm"];
const PRICE_UNITS = ["total", "per_sqft", "per_gaj", "per_acre"];
const DIRECTIONS = [
  { key: "N",  en: "North",       hi: "उत्तर",           icon: "↑" },
  { key: "NE", en: "North-East",  hi: "ईशान (उत्तर-पूर्व)", icon: "↗" },
  { key: "E",  en: "East",        hi: "पूर्व",           icon: "→" },
  { key: "SE", en: "South-East",  hi: "आग्नेय",          icon: "↘" },
  { key: "S",  en: "South",       hi: "दक्षिण",         icon: "↓" },
  { key: "SW", en: "South-West",  hi: "नैऋत्य",         icon: "↙" },
  { key: "W",  en: "West",        hi: "पश्चिम",         icon: "←" },
  { key: "NW", en: "North-West",  hi: "वायव्य",         icon: "↖" },
];

export default function AddProperty() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  const [form, setForm] = useState({
    title: "", description: "", category: "plot",
    size_value: "", size_unit: "gaj",
    price_value: "", price_unit: "total",
    address: "", city: "Jodhpur", state: "Rajasthan",
    latitude: null, longitude: null,
    bedrooms: "", bathrooms: "",
    contact_phone: "",
    facing_direction: "",
  });
  const [photoPaths, setPhotoPaths] = useState([]);
  const [paperPaths, setPaperPaths] = useState([]);
  const [modelPath, setModelPath] = useState(null);
  const [uploading, setUploading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/properties/${editId}`).then(({ data }) => {
      setForm({
        title: data.title || "", description: data.description || "", category: data.category || "plot",
        size_value: String(data.size_value ?? ""), size_unit: data.size_unit || "sqft",
        price_value: String(data.price_value ?? ""), price_unit: data.price_unit || "total",
        address: data.address || "", city: data.city || "", state: data.state || "",
        latitude: data.latitude ?? null, longitude: data.longitude ?? null,
        bedrooms: data.bedrooms != null ? String(data.bedrooms) : "",
        bathrooms: data.bathrooms != null ? String(data.bathrooms) : "",
        contact_phone: data.contact_phone || "",
        facing_direction: data.facing_direction || "",
      });
      setPhotoPaths(data.photo_paths || []);
      setPaperPaths(data.paper_paths || []);
      setModelPath(data.model_path || null);
    }).catch(() => toast.error("Property load failed")).finally(() => setLoadingEdit(false));
  }, [isEdit, editId]);

  if (authLoading || loadingEdit) return <div className="py-20 text-center text-muted-foreground">{t.common.loading}</div>;
  if (!user) return <Navigate to="/login" state={{ from: isEdit ? `/edit-property/${editId}` : "/sell" }} replace />;

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (files, kind, setPaths, multiple = true) => {
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      if (kind === "model") {
        setPaths(uploaded[0]);
      } else if (multiple) {
        setPaths((prev) => [...prev, ...uploaded]);
      } else {
        setPaths(uploaded);
      }
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.address || !form.city || !form.state || !form.size_value || !form.price_value) {
      toast.error("Please fill all required fields");
      return;
    }
    if (photoPaths.length === 0) {
      toast.error("Kam se kam 1 photo add karein");
      return;
    }
    if (!form.facing_direction) {
      toast.error("Facing direction chunna zaroori hai (Vastu auto-calculate ke liye)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        size_value: parseFloat(form.size_value),
        price_value: parseFloat(form.price_value),
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
        facing_direction: form.facing_direction || null,
        photo_paths: photoPaths,
        paper_paths: paperPaths,
        model_path: modelPath,
      };
      const { data } = isEdit
        ? await api.put(`/properties/${editId}`, payload)
        : await api.post("/properties", payload);
      toast.success(isEdit ? "Listing updated!" : "Property listed successfully!");
      nav(`/property/${data.id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(formatDetail(e.response?.data?.detail) || "Subscription required");
        nav("/plans");
        return;
      }
      toast.error(formatDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <div className="overline text-secondary">{isEdit ? "Edit Property" : t.nav.sell}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{isEdit ? "Update Your Listing" : t.add.title}</h1>
        <p className="text-sm text-muted-foreground mt-2">{isEdit ? "Details change karke save karein." : t.add.subtitle}</p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        <Card className="p-5 space-y-4">
          <div className="overline text-primary">1. Basic Info</div>
          <div>
            <Label>{t.add.titleLabel} *</Label>
            <Input value={form.title} onChange={(e) => upd("title", e.target.value)} className="mt-1.5" data-testid="add-title" required />
          </div>
          <div>
            <Label>{t.add.descLabel} *</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => upd("description", e.target.value)} className="mt-1.5" data-testid="add-desc" required />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>{t.add.category} *</Label>
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger className="mt-1.5" data-testid="add-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{t.categories[c]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(form.category === "ghar") && (
              <>
                <div>
                  <Label>{t.add.bedrooms}</Label>
                  <Input type="number" value={form.bedrooms} onChange={(e) => upd("bedrooms", e.target.value)} className="mt-1.5" data-testid="add-bedrooms" />
                </div>
                <div>
                  <Label>{t.add.bathrooms}</Label>
                  <Input type="number" value={form.bathrooms} onChange={(e) => upd("bathrooms", e.target.value)} className="mt-1.5" data-testid="add-bathrooms" />
                </div>
              </>
            )}
            <div>
              <Label>{t.add.contactPhone}</Label>
              <Input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} className="mt-1.5" data-testid="add-phone" />
            </div>
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="overline text-primary">2. Size &amp; Price</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>{t.add.size} *</Label>
              <Input type="number" step="0.01" value={form.size_value} onChange={(e) => upd("size_value", e.target.value)} className="mt-1.5" data-testid="add-size-value" required />
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={form.size_unit} onValueChange={(v) => upd("size_unit", v)}>
                <SelectTrigger className="mt-1.5" data-testid="add-size-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{SIZE_UNITS.map((u) => <SelectItem key={u} value={u}>{t.units[u]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t.add.price} (₹) *</Label>
              <Input type="number" step="0.01" value={form.price_value} onChange={(e) => upd("price_value", e.target.value)} className="mt-1.5" data-testid="add-price-value" required />
            </div>
            <div>
              <Label>Price Unit</Label>
              <Select value={form.price_unit} onValueChange={(v) => upd("price_unit", v)}>
                <SelectTrigger className="mt-1.5" data-testid="add-price-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{PRICE_UNITS.map((u) => <SelectItem key={u} value={u}>{t.units[u]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="overline text-primary">3. {t.property.location}</div>
          <div>
            <Label>{t.add.addressLabel} *</Label>
            <Input value={form.address} onChange={(e) => upd("address", e.target.value)} className="mt-1.5" data-testid="add-address" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t.add.city} *</Label>
              <Input value={form.city} onChange={(e) => upd("city", e.target.value)} className="mt-1.5" data-testid="add-city" required />
            </div>
            <div>
              <Label>{t.add.state} *</Label>
              <Input value={form.state} onChange={(e) => upd("state", e.target.value)} className="mt-1.5" data-testid="add-state" required />
            </div>
          </div>
          <div>
            <Label className="mb-1.5 block">{t.add.pickLocation}</Label>
            <MapPicker
              lat={form.latitude}
              lng={form.longitude}
              onPick={(lat, lng) => setForm((f) => ({ ...f, latitude: lat, longitude: lng }))}
              height={300}
            />
            {form.latitude != null && (
              <p className="text-xs text-muted-foreground mt-2" data-testid="picked-coords">
                Selected: {form.latitude.toFixed(5)}, {form.longitude.toFixed(5)}
              </p>
            )}
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="overline text-primary flex items-center gap-1.5">
            <Compass size={12} /> 4. वास्तु दिशा / Vastu Direction <span className="text-red-500">*</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t.common?.loading === "लोड हो रहा है…"
              ? "मुख्य द्वार / प्रवेश की दिशा चुनें (अनिवार्य) — वास्तु स्कोर अपने आप कैलकुलेट होगा।"
              : "Select main entrance / facing direction (required) — Vastu score is auto-calculated."}
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {DIRECTIONS.map((d) => {
              const active = form.facing_direction === d.key;
              return (
                <button
                  type="button"
                  key={d.key}
                  onClick={() => upd("facing_direction", active ? "" : d.key)}
                  className={`aspect-square rounded-lg border-2 grid place-items-center transition-all ${
                    active
                      ? "bg-primary text-primary-foreground border-primary shadow-md scale-105"
                      : "bg-card border-border hover:border-primary/60 text-foreground"
                  }`}
                  data-testid={`dir-${d.key}`}
                  title={`${d.en} / ${d.hi}`}
                >
                  <div className="text-2xl leading-none">{d.icon}</div>
                  <div className="text-[10px] mt-1 font-semibold uppercase tracking-wider">{d.key}</div>
                </button>
              );
            })}
          </div>
          {form.facing_direction ? (
            <VastuPanel category={form.category} direction={form.facing_direction} />
          ) : (
            <VastuPanel category={form.category} />
          )}
        </Card>

        <Card className="p-5 space-y-4">
          <div className="overline text-primary">5. Media</div>

          <div>
            <Label className="flex items-center gap-2"><ImagePlus size={14} />{t.add.photos} *</Label>
            <input type="file" multiple accept="image/*" className="mt-2 text-sm" onChange={(e) => upload(Array.from(e.target.files), "photo", setPhotoPaths, true)} data-testid="upload-photos" />
            {uploading.photo && <div className="text-xs text-muted-foreground mt-1"><Loader2 className="inline animate-spin" size={12}/> {t.add.uploading}</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              {photoPaths.map((p, i) => (
                <div key={p} className="relative w-20 h-20 rounded-md overflow-hidden border border-border">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} className="w-full h-full object-cover" alt="" />
                  <button type="button" onClick={() => setPhotoPaths((ps) => ps.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-black/70 text-white p-0.5 rounded-bl-md">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-2"><Boxes size={14} />{t.add.model3d}</Label>
            <input type="file" accept=".glb,.gltf" className="mt-2 text-sm" onChange={(e) => e.target.files[0] && upload([e.target.files[0]], "model", setModelPath, false)} data-testid="upload-model" />
            {uploading.model && <div className="text-xs text-muted-foreground mt-1"><Loader2 className="inline animate-spin" size={12}/> {t.add.uploading}</div>}
            {modelPath && (
              <div className="text-xs text-primary mt-2 flex items-center gap-2">
                ✔ 3D model uploaded
                <button type="button" onClick={() => setModelPath(null)} className="text-destructive underline">remove</button>
              </div>
            )}
          </div>

          <div>
            <Label className="flex items-center gap-2"><FileText size={14} />{t.add.papers}</Label>
            <input type="file" multiple accept="application/pdf,image/*" className="mt-2 text-sm" onChange={(e) => upload(Array.from(e.target.files), "paper", setPaperPaths, true)} data-testid="upload-papers" />
            {uploading.paper && <div className="text-xs text-muted-foreground mt-1"><Loader2 className="inline animate-spin" size={12}/> {t.add.uploading}</div>}
            <div className="mt-2 flex flex-wrap gap-2">
              {paperPaths.map((p, i) => (
                <div key={p} className="flex items-center gap-2 text-xs bg-accent px-3 py-1.5 rounded-full">
                  <FileText size={12} /> Paper #{i + 1}
                  <button type="button" onClick={() => setPaperPaths((ps) => ps.filter((_, idx) => idx !== i))} className="text-destructive">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => nav(-1)}>{t.common.cancel}</Button>
          <Button type="submit" className="bg-primary hover:bg-primary/90 rounded-full px-8" disabled={submitting} data-testid="publish-btn">
            {submitting ? <><Loader2 className="animate-spin mr-2" size={14} />{t.add.saving}</> : <><Upload size={14} className="mr-2" />{isEdit ? "Save Changes" : t.add.publishBtn}</>}
          </Button>
        </div>
      </form>
    </div>
  );
}
